import { useEffect, useRef } from "react";
import clsx from "clsx";
import { DeleteIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";

interface AuthPinProps {
  title: string;
  code: string;
  onCodeChange: (value: string) => void;
  isValid?: boolean;
}

function AuthPin({ title, code, onCodeChange, isValid }: AuthPinProps) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleButtonClick = (value: string) => {
    if (code.length < 4) {
      onCodeChange(code + value);
    }
  };

  const handleBackspace = () => {
    if (code.length > 0) {
      onCodeChange(code.slice(0, -1));
    }
  };

  const buttons = Array.from({ length: 9 }, (_, i) => (i + 1).toString());

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key >= "0" && event.key <= "9" && code.length < 4) {
        const buttonIndex = event.key === "0" ? 9 : parseInt(event.key, 10) - 1;
        const button = buttonRefs.current[buttonIndex];

        if (button) {
          button.classList.add("mz-state-active");
          setTimeout(() => button.classList.remove("mz-state-active"), 100);
        }

        onCodeChange(code + event.key);
      }

      if (event.key === "Backspace") {
        handleBackspace();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [code, onCodeChange]);

  return (
    <div className="text-center">
      <div className="mb-2.5">{title}</div>
      <div
        className="flex justify-center gap-1.5 variant-gray data-[invalid]:variant-danger variant-soft  data-[invalid]:animate-[--animation-shake-x]"
        data-valid={isValid ? "" : null}
        id="pin-field"
      >
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={clsx(
              "h-12 aspect-square bg-dark-800/80 flex items-center justify-center rounded-[0.5rem] text-base",
              { "text-white/60": !code[index] },
            )}
          >
            {code[index] ? "*" : ""}
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap justify-end max-w-40 mx-auto gap-1.5">
        {buttons.map((button, index) => (
          <Button
            key={button}
            ref={(el) => {
              buttonRefs.current[index] = el;
              return el;
            }}
            variant="surface"
            color="gray"
            icon
            className="h-12 text-base text-white/60 hover:text-white"
            onClick={() => handleButtonClick(button)}
          >
            {button}
          </Button>
        ))}
        <Button
          ref={(el) => {
            buttonRefs.current[9] = el;
            return el;
          }}
          variant="surface"
          color="gray"
          icon
          className="h-12 text-base text-white/60 hover:text-white"
          onClick={() => handleButtonClick("0")}
        >
          0
        </Button>
        <Button variant="link" color="gray" icon className="h-12" onClick={handleBackspace}>
          <DeleteIcon size={30} />
        </Button>
      </div>
    </div>
  );
}

export default AuthPin;
